<template>
  <v-sheet elevation="2" class="px-12 py-8 rounded white">
    <div class="mb-6">
      <p class="h6 pa-0 mb-0">
        {{ $t('settings.general_settings') }}
      </p>
      <p class="mt-2 pa-0 grey--text text--darken-2">
        {{ $t('site_settings.settings_subtitle') }}
      </p>
    </div>
    <v-form v-model="formOptions.valid" lazyValidation ref="settings">
      <v-text-field
        v-model="site.name"
        disabled
        outlined
        dense
        class="mb-2"
        data-testid="settings-textField-name"
        :label="`${$t('site_settings.site_name')}`"
      ></v-text-field>
      <v-select
        outlined
        required
        dense
        item-text="name"
        class="mb-2"
        v-model="site.language"
        data-testid="settings-textField-language"
        :rules="[v => !!v || $t('validation.form.this_field_is_required')]"
        :label="`${$t('organization.default_language')}*`"
        :items="languages"
      ></v-select>
      <v-select
        outlined
        required
        dense
        item-text="text"
        class="mb-2"
        v-model="site.currency"
        data-testid="settings-textField-currency"
        :label="`${$t('common.currency')}*`"
        :items="currencies"
      ></v-select>

      <v-autocomplete
        v-model="site.timezone"
        class="mb-2"
        clearable
        data-testid="settings-textField-timezone"
        dense
        outlined
        required
        :rules="[v => !!v || $t('validation.form.this_field_is_required')]"
        :label="`${$t('site_settings.timezone')}*`"
        :items="timezoneOptions"
      ></v-autocomplete>
    </v-form>
    <div class="text-right">
      <Button
        outlined
        size="large"
        color="grey"
        data-testid="settings-button-reset"
        class="me-2"
        :disabled="isLoading || !siteHasChanged"
        :text="$t('common.button.discard_changes')"
        @click="$emit('reset')"
      />
      <Button
        size="large"
        color="primary"
        data-testid="settings-button-save"
        :loading="isLoading"
        :disabled="isLoading || !site.name"
        :text="$t('common.button.save')"
        @click="$emit('update')"
      />
    </div>
  </v-sheet>
</template>
<script>
import mixin from '@/mixins/mixin.vue'
import { utils } from 'frontend-js-shared'
import globalCurrencies from '@/locales/currencies.json'
import access from '@/lib/access.js'
import { mapActions } from 'vuex'

import Button from '@/components/UI/Button'

export default {
  name: 'General',

  mixins: [mixin],

  props: {
    site: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    siteHasChanged: {
      type: Boolean,
      required: true,
    },
    organizationId: {
      type: String,
      required: true,
    },
  },

  components: {
    Button,
  },

  data() {
    return {
      formOptions: {
        valid: true,
      },
      timezoneOptions: [],
      organization: {},
      globalCurrencies: Object.values(globalCurrencies).map(
        ({ name, code }) => ({
          name,
          value: code,
          text: `${code} - ${name}`,
        })
      ),
    }
  },

  async mounted() {
    this.organization = await this.fetchOrganization()
    this.timezoneOptions = await utils.dates.humanizeTimezones()
  },

  computed: {
    languages() {
      return this.getLanguages()
    },

    currencies() {
      return this.globalCurrencies.filter(this.onlyOrganizationCurrencies)
    },
    isAdmin() {
      const account = this.$store.getters.account
      return access.isAdmin(account)
    },
  },

  methods: {
    ...mapActions({
      getOrganization: 'getOrganization',
    }),

    validate() {
      return this.$refs.settings.validate()
    },

    onlyOrganizationCurrencies(currency) {
      const values = this.organization?.allowedSiteCurrencies?.map(
        c => c?.currency
      )
      return values?.includes(currency?.value)
    },

    async fetchOrganization() {
      try {
        const response = await this.getOrganization({
          organizationId: this.organizationId,
        })
        return response
      } catch (err) {
        this.handleRequestError(err)
      }
    },
  },
}
</script>
